import type {
  ActionPlan,
  Milestone,
  Task,
  MoveTaskPayload,
  MoveTaskDto,
  UpdateActionPlanPayload,
  UpdateTaskPayload,
  UpdateMilestonePayload,
} from '../types/action-plan';
import type { LoadMilestoneDto } from '../../api/mutual-action-plan/types/load-milestone-dto';

export default class ActionPlanHelper {
  static rearrangeMilestones = (milestones: Milestone[], oldPos: number, newPos: number) => {
    const newMilestones = [...milestones];
    const [milestoneToMove] = newMilestones.splice(oldPos, 1);
    newMilestones.splice(newPos, 0, milestoneToMove);
    return newMilestones;
  };

  static moveMilestone = ({
    actionPlans,
    actionPlanId,
    movePayload: { currentIndex, targetIndex },
  }: UpdateMilestonePayload) =>
    actionPlans.map(plan =>
      plan.id === actionPlanId
        ? {
            ...plan,
            milestones: ActionPlanHelper.rearrangeMilestones(
              plan.milestones,
              currentIndex,
              targetIndex
            ),
          }
        : plan
    );

  static getMoveTaskPayload = (
    actionPlans: ActionPlan[],
    actionPlanId: string,
    { sourceMilestoneId, sourceTaskId, targetMilestoneId, targetTaskId, append }: MoveTaskPayload
  ) => {
    const actionPlan = actionPlans.find(({ id }) => id === actionPlanId);
    const targetMilestone = actionPlan?.milestones?.find(({ id }) => id === targetMilestoneId);
    const tasks = targetMilestone.tasks.filter(({ id }) => id !== sourceTaskId);
    const targetIndex = tasks?.length > 0 ? tasks.findIndex(({ id }) => id === targetTaskId) : 0;

    return {
      id: sourceTaskId,
      targetIndex: append ? targetIndex + 1 : targetIndex,
      targetMilestoneId: targetMilestoneId !== sourceMilestoneId ? targetMilestoneId : undefined,
    };
  };

  static insertIntoArray = (array: Task[], index: number, item: Task) => [
    ...array.slice(0, index),
    item,
    ...array.slice(index),
  ];

  static rearrangeTasks = (
    milestones: Milestone[],
    milestoneId: string,
    { id: taskId, targetIndex, targetMilestoneId }: MoveTaskDto
  ) => {
    const sourceMilestone = milestones.find(({ id }) => id === milestoneId);
    const taskToMove = sourceMilestone.tasks.find(({ id }) => id === taskId);

    const milestonesWithoutMovedTask = milestones.map(milestone => ({
      ...milestone,
      tasks: milestone.tasks.filter(({ id }) => id !== taskId),
    }));

    return milestonesWithoutMovedTask.map(milestone =>
      milestone.id === targetMilestoneId || (!targetMilestoneId && milestone.id === milestoneId)
        ? {
            ...milestone,
            tasks: ActionPlanHelper.insertIntoArray(milestone.tasks, targetIndex, taskToMove),
          }
        : milestone
    );
  };

  static moveTask = ({
    actionPlans,
    actionPlanId,
    milestoneId,
    movePayload: { id, targetIndex, targetMilestoneId },
  }: UpdateTaskPayload) =>
    actionPlans.map(plan =>
      plan.id === actionPlanId
        ? {
            ...plan,
            milestones: ActionPlanHelper.rearrangeTasks(plan.milestones, milestoneId, {
              id,
              targetIndex,
              targetMilestoneId,
            }),
          }
        : plan
    );

  static updateMilestone = ({
    actionPlans,
    actionPlanId,
    milestoneId,
    milestone: targetMilestone,
  }: UpdateMilestonePayload) =>
    actionPlans.map(plan =>
      actionPlanId === plan.id
        ? {
            ...plan,
            milestones: plan.milestones.map(milestone =>
              milestone.id === milestoneId ? { ...milestone, ...targetMilestone } : milestone
            ),
          }
        : plan
    );

  static addMilestone = ({ actionPlans, actionPlanId, milestone }: UpdateMilestonePayload) =>
    actionPlans.map(plan => {
      const newMilestones = plan.milestones ? [...plan.milestones, milestone] : [milestone];
      return plan.id === actionPlanId ? { ...plan, milestones: newMilestones } : plan;
    });

  static deleteMilestone = ({ actionPlans, actionPlanId, milestoneId }: UpdateMilestonePayload) =>
    actionPlans.map(plan =>
      plan.id === actionPlanId
        ? {
            ...plan,
            milestones: plan.milestones.filter(milestone => milestone.id !== milestoneId),
          }
        : plan
    );

  static updateTask = ({
    actionPlans,
    actionPlanId,
    milestoneId,
    taskId,
    task: targetTask,
  }: UpdateTaskPayload) =>
    actionPlans.map(plan =>
      actionPlanId === plan.id
        ? {
            ...plan,
            milestones: plan.milestones.map(milestone =>
              milestone.id === milestoneId
                ? {
                    ...milestone,
                    tasks: milestone.tasks.map(task =>
                      task.id === taskId ? { ...task, ...targetTask } : task
                    ),
                  }
                : milestone
            ),
          }
        : plan
    );

  static addTask = ({ actionPlans, actionPlanId, milestoneId, task }: UpdateTaskPayload) =>
    actionPlans.map(plan =>
      actionPlanId === plan.id
        ? {
            ...plan,
            milestones: plan.milestones.map(milestone =>
              milestone.id === milestoneId
                ? {
                    ...milestone,
                    tasks: [...milestone.tasks, task],
                  }
                : milestone
            ),
          }
        : plan
    );

  static deleteTask = ({ actionPlans, actionPlanId, milestoneId, taskId }: UpdateTaskPayload) =>
    actionPlans.map(plan =>
      plan.id === actionPlanId
        ? {
            ...plan,
            milestones: plan.milestones.map(milestone =>
              milestone.id === milestoneId
                ? {
                    ...milestone,
                    tasks: milestone.tasks.filter(task => task.id !== taskId),
                  }
                : milestone
            ),
          }
        : plan
    );

  static handleLoadedMilestones = (
    actionPlans: ActionPlan[],
    loadedMilestones: Milestone[],
    payload: LoadMilestoneDto
  ) =>
    actionPlans.map(plan =>
      plan.id === payload.actionPlanId
        ? {
            ...plan,
            milestones:
              payload.offset !== 0 ? [...plan.milestones, ...loadedMilestones] : loadedMilestones,
          }
        : plan
    );

  static updatePlan = ({
    actionPlans,
    actionPlanId,
    actionPlan: updatedPlan,
  }: UpdateActionPlanPayload) =>
    actionPlans.map(plan => (plan.id === actionPlanId ? { ...plan, ...updatedPlan } : plan));
}
